import { useState, useEffect } from "react";
import type { Comment } from "@/services/SessionsService";
import { SessionsService } from "@/services/SessionsService";

interface CommentsTabProps {
  sessionId: number;
  onCommentsCountChange?: (count: number) => void;
}

export const CommentsTab: React.FC<CommentsTabProps> = ({
  sessionId,
  onCommentsCountChange,
}) => {
  const [comments, setComments] = useState<Comment[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editText, setEditText] = useState("");
  const [deletingId, setDeletingId] = useState<number | null>(null);

  useEffect(() => {
    loadComments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const loadComments = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await SessionsService.listSessionComments(sessionId);
      setComments(data);
    } catch (err) {
      console.error("Error loading comments:", err);
      setError("Failed to load comments");
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = (comment: Comment) => {
    setEditingId(comment.id);
    setEditText(comment.comment);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditText("");
  };

  const handleSaveEdit = async (commentId: number) => {
    try {
      await SessionsService.updateComment(commentId, editText);
      setComments((prev) =>
        prev.map((c) => (c.id === commentId ? { ...c, comment: editText } : c))
      );
      setEditingId(null);
      setEditText("");
    } catch (err) {
      console.error("Error updating comment:", err);
    }
  };

  const handleDelete = async (commentId: number) => {
    try {
      await SessionsService.deleteComment(commentId);
      const updated = comments.filter((c) => c.id !== commentId);
      setComments(updated);
      setDeletingId(null);
      onCommentsCountChange?.(updated.length);
    } catch (err) {
      console.error("Error deleting comment:", err);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <svg
          className="animate-spin h-8 w-8 text-cyan-500"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12 text-gray-500">
        <p>{error}</p>
      </div>
    );
  }

  if (comments.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500">No comments</div>
    );
  }

  return (
    <div className="space-y-3">
      {comments.map((comment) => (
        <div
          key={comment.id}
          className="bg-gray-50 rounded-lg p-4 flex gap-3"
        >
          {/* Avatar */}
          <div className="flex-shrink-0">
            {comment.user.profilepicid ? (
              <img
                src={SessionsService.getProfileImageUrl(
                  comment.user.profilepicid
                )}
                alt={comment.user.name}
                className="w-8 h-8 rounded-full object-cover"
              />
            ) : (
              <div className="w-8 h-8 rounded-full bg-gray-300 flex items-center justify-center text-xs font-medium text-gray-600">
                {comment.user.name?.charAt(0)?.toUpperCase() || "?"}
              </div>
            )}
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm font-medium text-gray-900">
                {comment.user.name}
              </span>
              <span className="text-xs text-gray-400">
                {SessionsService.formatDate(comment.datetime)}
              </span>
            </div>

            {editingId === comment.id ? (
              <div className="space-y-2">
                <textarea
                  value={editText}
                  onChange={(e) => setEditText(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-cyan-500 focus:border-cyan-500"
                  rows={3}
                />
                <div className="flex gap-2">
                  <button
                    onClick={() => handleSaveEdit(comment.id)}
                    disabled={!editText.trim()}
                    className="px-3 py-1 text-xs font-medium text-white bg-cyan-600 rounded hover:bg-cyan-700 disabled:opacity-50"
                  >
                    Save
                  </button>
                  <button
                    onClick={handleCancelEdit}
                    className="px-3 py-1 text-xs font-medium text-gray-600 bg-gray-200 rounded hover:bg-gray-300"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <>
                <p className="text-sm text-gray-700">{comment.comment}</p>

                {deletingId === comment.id ? (
                  <div className="flex items-center gap-2 mt-2">
                    <span className="text-xs text-red-600">Delete this comment?</span>
                    <button
                      onClick={() => handleDelete(comment.id)}
                      className="px-2 py-0.5 text-xs font-medium text-white bg-red-600 rounded hover:bg-red-700"
                    >
                      Confirm
                    </button>
                    <button
                      onClick={() => setDeletingId(null)}
                      className="px-2 py-0.5 text-xs font-medium text-gray-600 bg-gray-200 rounded hover:bg-gray-300"
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <div className="flex gap-2 mt-2">
                    <button
                      onClick={() => handleEdit(comment)}
                      className="text-xs text-cyan-600 hover:text-cyan-700"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => setDeletingId(comment.id)}
                      className="text-xs text-red-500 hover:text-red-600"
                    >
                      Delete
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};
